import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMyContract, signContract, downloadMyContractTemplate, downloadSignedContractTutor, getComplianceCheck } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Sprout, FileText, CheckCircle, XCircle, Download, PenTool,
  Loader2, ChevronLeft, AlertTriangle, Shield, Clock, User, LogOut, RefreshCw
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export default function ContractPage() {
  const { user, logout } = useAuth();
  const [contractData, setContractData] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [signatureName, setSignatureName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);

  const loadData = useCallback(async () => {
    setLoadError(null);
    try {
      // Load independently so one failure doesn't block the other
      const [contractRes, complianceRes] = await Promise.allSettled([
        getMyContract(), getComplianceCheck()
      ]);

      if (contractRes.status === 'fulfilled') {
        setContractData(contractRes.value.data);
      } else {
        console.error('Contract load failed:', contractRes.reason);
        setLoadError('Failed to load contract data. Please try refreshing the page.');
      }

      if (complianceRes.status === 'fulfilled') {
        setCompliance(complianceRes.value.data);
      }
    } catch (err) {
      console.error('Contract page error:', err);
      setLoadError('Something went wrong. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSign = async () => {
    if (!signatureName.trim()) {
      toast.error('Please enter your full name as signature');
      return;
    }
    if (!agreed) {
      toast.error('Please confirm you have read the contract');
      return;
    }
    setSigning(true);
    try {
      await signContract(signatureName.trim());
      toast.success('Contract signed successfully!');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sign contract');
    } finally {
      setSigning(false);
    }
  };

  const handleDownload = async () => {
    try {
      await downloadMyContractTemplate();
      toast.success('Contract downloaded');
    } catch (err) {
      toast.error('Failed to download contract');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
      </div>
    );
  }

  const status = contractData?.status;

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#0A0A0A] rounded-xl flex items-center justify-center">
                <Sprout className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-lg font-bold text-[#111827] tracking-tight">My Contract</h1>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/dashboard">
                <Button variant="outline" size="sm" className="text-xs border-gray-200">
                  <ChevronLeft className="w-3 h-3 mr-1" /> Dashboard
                </Button>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="text-xs border-gray-200">
                    <User className="w-3 h-3 mr-1" /> {user?.name}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild><Link to="/profile">Profile</Link></DropdownMenuItem>
                  <DropdownMenuItem onClick={logout}><LogOut className="w-3 h-3 mr-2" />Logout</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Compliance Status Card */}
        {compliance && (
          <Card className="border border-black/10" data-testid="compliance-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#002FA7]" /> Compliance Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="flex items-center gap-2">
                  {compliance.onboarding_approved
                    ? <CheckCircle className="w-4 h-4 text-[#10B981]" />
                    : <XCircle className="w-4 h-4 text-[#EF4444]" />}
                  <span className="text-sm">Onboarding</span>
                </div>
                <div className="flex items-center gap-2">
                  {compliance.contract_signed
                    ? <CheckCircle className="w-4 h-4 text-[#10B981]" />
                    : <XCircle className="w-4 h-4 text-[#EF4444]" />}
                  <span className="text-sm">Contract</span>
                </div>
                <div className="flex items-center gap-2">
                  {compliance.tutor_type
                    ? <CheckCircle className="w-4 h-4 text-[#10B981]" />
                    : <XCircle className="w-4 h-4 text-[#EF4444]" />}
                  <span className="text-sm">Tutor Type</span>
                </div>
                <div className="flex items-center gap-2">
                  {compliance.hourly_rate
                    ? <CheckCircle className="w-4 h-4 text-[#10B981]" />
                    : <XCircle className="w-4 h-4 text-[#EF4444]" />}
                  <span className="text-sm">Pay Rate</span>
                </div>
              </div>
              {compliance.fully_compliant && (
                <div className="mt-3 p-2 bg-green-50 rounded-lg border border-green-100 text-center">
                  <p className="text-sm font-medium text-green-700">You are fully compliant and ready for scheduling</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Error State */}
        {loadError && !contractData && (
          <Card className="border border-red-200 bg-red-50/30" data-testid="contract-error">
            <CardContent className="p-6 text-center">
              <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-[#111827] mb-2">Unable to Load Contract</h2>
              <p className="text-sm text-[#4B5563] mb-4">{loadError}</p>
              <Button onClick={() => { setLoading(true); loadData(); }} variant="outline" size="sm" data-testid="retry-contract-btn">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Try Again
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Contract Status */}
        {status === 'signed' && (
          <Card className="border border-green-200 bg-green-50/30" data-testid="contract-signed">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#10B981]" /> Contract Signed
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={async () => {
                    try { await downloadSignedContractTutor(); toast.success('PDF downloaded'); }
                    catch { toast.error('Failed to download'); }
                  }}
                  data-testid="download-signed-pdf"
                >
                  <Download className="w-3.5 h-3.5 mr-1" /> Download PDF
                </Button>
              </div>
              <p className="text-xs text-[#4B5563] mt-1">
                Signed by: <strong className="italic">{contractData.contract.signature_name}</strong> on {new Date(contractData.contract.signed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
              <Badge className="mt-2 bg-green-100 text-green-800 border-green-200 w-fit">
                {contractData.contract.contract_type === 'main_tutor' ? 'Main Tutor Contract' : 'Standby Tutor Contract'}
              </Badge>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Contract Title */}
              <div className="p-3 bg-white rounded-lg border border-green-100">
                <p className="text-sm font-semibold text-[#111827]">{contractData.contract.contract_title || 'Employment Contract'}</p>
              </div>

              {/* Contract Terms */}
              {contractData.contract.contract_terms && (
                <div className="border border-gray-200 rounded-lg">
                  <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 rounded-t-lg">
                    <p className="text-xs font-semibold text-[#374151]">CONTRACT TERMS</p>
                  </div>
                  <div className="p-4 max-h-[400px] overflow-y-auto bg-white">
                    <pre className="text-sm text-[#111827] whitespace-pre-wrap font-sans leading-relaxed" data-testid="signed-contract-terms">
                      {contractData.contract.contract_terms}
                    </pre>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {status === 'no_type' && (
          <Card className="border border-amber-200 bg-amber-50/30" data-testid="contract-no-type">
            <CardContent className="p-6 text-center">
              <Clock className="w-12 h-12 text-amber-500 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-[#111827] mb-2">Awaiting Configuration</h2>
              <p className="text-sm text-[#4B5563]">{contractData.message}</p>
            </CardContent>
          </Card>
        )}

        {status === 'no_template' && (
          <Card className="border border-amber-200 bg-amber-50/30" data-testid="contract-no-template">
            <CardContent className="p-6 text-center">
              <FileText className="w-12 h-12 text-amber-500 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-[#111827] mb-2">No Contract Available</h2>
              <p className="text-sm text-[#4B5563]">{contractData.message}</p>
            </CardContent>
          </Card>
        )}

        {status === 'pending' && (
          <Card className="border border-[#002FA7]/20" data-testid="contract-pending">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <PenTool className="w-4 h-4 text-[#002FA7]" /> Sign Your Contract
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Template Info */}
              <div className="p-4 bg-[#F9FAFB] rounded-lg border border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="w-8 h-8 text-[#002FA7]" />
                    <div>
                      <p className="font-medium text-sm text-[#111827]">{contractData.template.template_name}</p>
                      <p className="text-xs text-[#9CA3AF]">
                        {contractData.template.template_type === 'main_tutor' ? 'Main Tutor Contract' : 'Standby Tutor Contract'}
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleDownload} data-testid="download-contract-btn">
                    <Download className="w-3.5 h-3.5 mr-1" /> Download
                  </Button>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg mb-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-800">
                      Please download and read the contract carefully before signing. By signing below, you confirm you have read, understood, and agree to all terms.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2 mb-4">
                  <input
                    type="checkbox"
                    id="agree-check"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-1 rounded border-gray-300"
                    data-testid="agree-checkbox"
                  />
                  <label htmlFor="agree-check" className="text-sm text-[#111827]">
                    I confirm I have downloaded, read, and understood the contract in full
                  </label>
                </div>

                <div className="mb-4">
                  <Label>Digital Signature (Full Legal Name)</Label>
                  <Input
                    value={signatureName}
                    onChange={(e) => setSignatureName(e.target.value)}
                    placeholder="Enter your full name as digital signature"
                    className="mt-1"
                    data-testid="signature-input"
                  />
                </div>

                <Button
                  onClick={handleSign}
                  disabled={signing || !agreed || !signatureName.trim()}
                  className="w-full bg-[#002FA7] hover:bg-[#002482]"
                  data-testid="sign-contract-btn"
                >
                  {signing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PenTool className="w-4 h-4 mr-2" />}
                  Sign Contract
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Assigned (personalized) contract — show terms and sign */}
        {status === 'assigned' && (
          <Card className="border border-[#002FA7]/20" data-testid="contract-assigned">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <PenTool className="w-4 h-4 text-[#002FA7]" /> Review & Sign Your Contract
              </CardTitle>
              <p className="text-xs text-[#4B5563] mt-1">
                Assigned by <strong>{contractData.contract.assigned_by_name}</strong> on {new Date(contractData.contract.assigned_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Contract Title */}
              <div className="p-3 bg-[#F9FAFB] rounded-lg border border-gray-100">
                <p className="text-sm font-semibold text-[#111827]">{contractData.contract.contract_title || 'Employment Contract'}</p>
                {contractData.template && (
                  <div className="flex items-center gap-2 mt-2">
                    <FileText className="w-4 h-4 text-[#002FA7]" />
                    <span className="text-xs text-[#9CA3AF]">Based on: {contractData.template.template_name}</span>
                    <Button variant="outline" size="sm" className="ml-auto h-7 text-xs" onClick={handleDownload} data-testid="download-template-btn">
                      <Download className="w-3 h-3 mr-1" /> Template
                    </Button>
                  </div>
                )}
              </div>

              {/* Contract Terms */}
              {contractData.contract.contract_terms ? (
                <div className="border border-gray-200 rounded-lg">
                  <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 rounded-t-lg">
                    <p className="text-xs font-semibold text-[#374151]">CONTRACT TERMS</p>
                  </div>
                  <div className="p-4 max-h-[400px] overflow-y-auto">
                    <pre className="text-sm text-[#111827] whitespace-pre-wrap font-sans leading-relaxed" data-testid="contract-terms-text">
                      {contractData.contract.contract_terms}
                    </pre>
                  </div>
                </div>
              ) : (
                <div className="border border-amber-200 rounded-lg bg-amber-50/30 p-4 text-center" data-testid="contract-terms-empty">
                  <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                  <p className="text-sm text-[#4B5563]">No contract terms have been provided yet.</p>
                  <p className="text-xs text-[#9CA3AF] mt-1">Please contact your administrator to complete the contract details.</p>
                </div>
              )}

              {/* Signing Section */}
              <div className="border-t border-gray-100 pt-4">
                <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg mb-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-800">
                      Please read the contract terms above carefully before signing. By signing below, you confirm you have read, understood, and agree to all terms.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2 mb-4">
                  <input
                    type="checkbox"
                    id="agree-assigned"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-1 rounded border-gray-300"
                    data-testid="agree-checkbox"
                  />
                  <label htmlFor="agree-assigned" className="text-sm text-[#111827]">
                    I confirm I have read and understood the contract terms in full
                  </label>
                </div>

                <div className="mb-4">
                  <Label>Digital Signature (Full Legal Name)</Label>
                  <Input
                    value={signatureName}
                    onChange={(e) => setSignatureName(e.target.value)}
                    placeholder="Enter your full name as digital signature"
                    className="mt-1"
                    data-testid="signature-input"
                  />
                </div>

                <Button
                  onClick={handleSign}
                  disabled={signing || !agreed || !signatureName.trim()}
                  className="w-full bg-[#002FA7] hover:bg-[#002482]"
                  data-testid="sign-contract-btn"
                >
                  {signing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PenTool className="w-4 h-4 mr-2" />}
                  Sign Contract
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* No status and no error — something unexpected */}
        {!status && !loadError && !loading && (
          <Card className="border border-gray-200" data-testid="contract-unknown">
            <CardContent className="p-6 text-center">
              <FileText className="w-12 h-12 text-[#9CA3AF] mx-auto mb-3" />
              <h2 className="text-xl font-bold text-[#111827] mb-2">No Contract Information</h2>
              <p className="text-sm text-[#4B5563] mb-4">Unable to determine your contract status. Please contact your administrator.</p>
              <Button onClick={() => { setLoading(true); loadData(); }} variant="outline" size="sm" data-testid="retry-contract-btn">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
